import * as React from 'react'
import { View, FlatList, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import AdminPlanCard from '@components/card/AdminPlanCard'
import Picker from '@components/picker/Picker'
import TextInput from '@components/textInput/TextInput'
import Loader from '@components/loader/Loader'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as AdminStore from '@store/admin/admin'

import api from '@api/api'
import Logger from '@utils/logger'
import sanitize from '@utils/Sanitizer'

import { createEvent, createStore } from 'effector'
// save current site selected
const setsiteId = createEvent<string>('setsiteId')
const SiteIdStore = createStore<string>('').on(setsiteId, (_, next) => next)

const AdminPlanListScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const site = useReducer(SiteStore.store, (s) => s.site)
  const sites = useReducer(SiteStore.store, (s) => s.sites)

  const [status, setStatus] = React.useState<'fetched' | 'loading'>('loading')
  const [search, setSearch] = React.useState('')

  const savedSiteId = useReducer(SiteIdStore, (s) => s)
  const siteId = savedSiteId || site?.id

  const plans = useReducer(AdminStore.store, (s) => s.plans)
  const spaces = useReducer(AdminStore.store, (s) => s.spaces)

  const items = React.useMemo(
    () =>
      spaces.filter(
        (s) => !!plans.find((p) => s.id === p.localisationId) && sanitize(s.libelle).includes(sanitize(search))
      ),
    [spaces, plans, search]
  )

  const options = React.useMemo(
    () => sites.map((s) => ({ label: i18n.t(`screens.site.sites.${s.codeLieu}`), value: s.id })),
    [sites]
  )

  React.useEffect(() => {
    if (siteId) {
      setStatus('loading')

      Promise.all([api.localisation.all(siteId), api.plan.all(siteId)])
        .then(([places, pls]) => {
          AdminStore.actions.setSpaces(places.filter((p) => !p.parentId))
          AdminStore.actions.setPlans(pls)
        })
        .catch(Logger.error)
        .finally(() => setStatus('fetched'))
    }
  }, [siteId])

  const AddPlanButton: HeaderButton = {
    label: i18n.t('screens.admin.plans.buttons.add'),
    onClick: () => navigation.push('/admin/plans/form/space'),
  }

  const renderCard = ({ item }: { item: LocalisationResponse }) => {
    return (
      <CardContainer activeOpacity={0.9} onPress={() => navigation.push(`/admin/plans/${item.id}`)}>
        <AdminPlanCard space={item} />
      </CardContainer>
    )
  }

  const renderSearch = () => (
    <>
      <SearchContainer>
        <TextInput
          onChange={setSearch}
          searchStyle="onDark"
          placeholder={i18n.t('screens.incidentList.search')}
          left={{ name: 'search' }}
          value={search}
        />
      </SearchContainer>
      <SearchContainer>
        <Picker
          value={options.find((s) => s.value === siteId)}
          options={options}
          onChange={(next) => setsiteId(next && next.value)}
          disabled={status === 'loading'}
          placeholder={i18n.t('components.drawer.sitePlaceholder')}
        />
      </SearchContainer>
    </>
  )

  return (
    <Root
      header={isPhone ? i18n.t('screens.admin.plans.title') : undefined}
      renderSubHeader={isPhone ? renderSearch : undefined}
      onLeft={navigation.goBack}
      right="add"
      onRight={AddPlanButton.onClick}
      fixed>
      {!isPhone && (
        <HeaderContainer>
          <Header title={i18n.t('screens.admin.plans.title')} renderBottom={renderSearch} buttons={[AddPlanButton]} />
        </HeaderContainer>
      )}
      {status === 'loading' ? (
        <Loading>
          <Loader />
        </Loading>
      ) : (
        <FlatList
          data={items}
          renderItem={renderCard}
          keyExtractor={(item) => item.id}
          contentContainerStyle={{
            flexWrap: 'wrap',
            flexDirection: isPhone ? 'column' : 'row',
            marginVertical: 20,
            marginHorizontal: isPhone ? 16 : 25,
            justifyContent: isPhone ? 'center' : 'flex-start',
          }}
          ListEmptyComponent={<MissingInfo>{i18n.t('screens.admin.plans.empty')}</MissingInfo>}
          showsVerticalScrollIndicator={false}
          extraData={spaces}
        />
      )}
    </Root>
  )
}

export default AdminPlanListScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const CardContainer = styled(TouchableOpacity)<{ disabled?: boolean }>`
  margin-bottom: 10px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin: 4px;` : `margin-right: 16px;`)};
`

const SearchContainer = styled(View)`
  margin-top: ${(props) => (props.theme.breakpoint === 'phone' ? 12 : 16)}px;
`

const Loading = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`
